import { queryLocationsWithInterval } from '@hobs/actions/locations';
import * as Yup from 'yup';
import { bulkChangeLocation } from '@hobs/actions/serviceRequests';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import LocationDetails from '@hobs/components/LocationDetails';
import { RaisedButton } from '@hobs/components/Buttons';
import Toast from '@hobs/components/Toast';
import { H5 } from '@hobs/components/Headings';
import Styles from './BulkLocationChange.module.scss';

const BulkLocationChange = ({
  //props
  getSelectedServiceRequestIds,
  oData,
  onSuccess,

  //connect
  queryLocationsWithInterval,
  locations,
  bulkChangeLocation,

  t
}) => {
  const initialLocation = useRef({
    original: {
      latitude: '',
      longitude: '',
      name: '',
      region: { name: '', regionId: '' },
      site: ''
    }
  });

  const doSelectedServiceRequestsHaveSameLocation = () => {
    const selectedSRIds = getSelectedServiceRequestIds();
    const selectedRowData = oData.data.filter(x =>
      selectedSRIds.find(y => y === x.ServiceRequestId)
    );

    if (selectedRowData.length === 0) {
      return true;
    }

    const locations = [
      ...new Set(selectedRowData.map(x => `${x.Location}${x.Site}${x.Region}`))
    ];
    const transportDeliveryLocations = [
      ...new Set(
        selectedRowData
          .filter(x => x.LocationTo)
          .map(x => `${x.LocationTo}${x.SiteTo}${x.RegionTo}`)
      )
    ];

    return locations.length === 1 && transportDeliveryLocations.length <= 1;
  };

  const hasTransportServices = () => {
    const selectedSRIds = getSelectedServiceRequestIds();
    const selectedRowData = oData.data.filter(x =>
      selectedSRIds.find(y => y === x.ServiceRequestId)
    );

    return (
      selectedRowData.filter(
        x => x.LocationTo !== null && x.LocationTo !== undefined
      ).length > 0
    );
  };

  const handleChangeLocation = async values => {
    if (
      !values.locationFrom.original ||
      !values.locationFrom.original.locationId
    )
      return;

    const locationIdFrom = values.locationFrom.original.locationId;
    const commentFrom = values.locationCommentFrom;

    const locationIdTo = values.locationTo
      ? values.locationTo.original.locationId
      : locationIdFrom;
    const commentTo = values.locationTo
      ? values.locationCommentTo
      : commentFrom;

    const selectedSRIds = getSelectedServiceRequestIds();

    const response = await bulkChangeLocation(
      locationIdFrom,
      commentFrom,
      locationIdTo,
      commentTo,
      selectedSRIds
    );

    if (response.success) {
      Toast.success(t('Locations changed'));
      onSuccess();
    } else {
      Toast.warning(response.payload.message);
    }
  };

  if (!getSelectedServiceRequestIds)
    return (
      <div className={Styles.locations_warning}>
        In order to bulk change locations - select specific service requests
        manually
      </div>
    );

  return (
    <>
      {!doSelectedServiceRequestsHaveSameLocation() && (
        <div className={Styles.locations_warning}>
          In order to bulk change locations - all selected service requests must
          have the same location (pickup location) and delivery location
        </div>
      )}
      {doSelectedServiceRequestsHaveSameLocation() && (
        <Formik
          validationSchema={Yup.object().shape({
            locationFrom: Yup.object()
              .required(t('Location is required'))
              .nullable(),
            locationTo: !hasTransportServices()
              ? Yup.object()
                  .nullable()
                  .optional()
              : Yup.object()
                  .required(t('Location is required'))
                  .nullable()
          })}
          onSubmit={handleChangeLocation}
          enableReinitialize={true}
          initialValues={{ locationFrom: null, locationTo: null }}
        >
          {({ isSubmitting, setFieldValue }) => (
            <Form>
              <H5>
                {t(
                  'Location' +
                    (hasTransportServices() ? ' (Transport Pickup)' : '')
                )}
              </H5>
              <LocationDetails
                setFieldValue={setFieldValue}
                schema={{
                  location: 'locationFrom',
                  locationComment: 'locationCommentFrom'
                }}
                locationItems={locations.map(item => ({
                  value: item.code,
                  title: item.name,
                  original: item
                }))}
                onQueryChange={query => queryLocationsWithInterval(query)}
                initialLocation={initialLocation.current}
              />
              {hasTransportServices() && (
                <>
                  <H5>{t('Delivery location')}</H5>
                  <LocationDetails
                    setFieldValue={setFieldValue}
                    locationItems={locations.map(item => ({
                      value: item.code,
                      title: item.name,
                      original: item
                    }))}
                    schema={{
                      location: 'locationTo',
                      locationComment: 'locationCommentTo'
                    }}
                    onQueryChange={query => queryLocationsWithInterval(query)}
                    initialLocation={initialLocation.current}
                  />
                </>
              )}
              <div className={Styles.locations_button}>
                <RaisedButton type="submit" disabled={isSubmitting}>
                  {t('Set Location')}
                </RaisedButton>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

BulkLocationChange.propTypes = {
  getSelectedServiceRequestIds: PropTypes.func,
  oData: PropTypes.array.isRequired,
  onSuccess: PropTypes.func.isRequired,

  queryLocationsWithInterval: PropTypes.func,
  bulkChangeLocation: PropTypes.func,
  locations: PropTypes.array,
  t: PropTypes.func
};

const mapStateToProps = state => ({
  locations: state.locations.query
});

const mapDispatchToProps = {
  queryLocationsWithInterval,
  bulkChangeLocation
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(BulkLocationChange));
